/**
 * MobileEmploymentPromisesList
 *
 * Mobile card-based replacement for EmploymentPromisesView.
 * Manages its own data fetching (dispatches getEmploymentPromises)
 * and includes add/edit modals just like the desktop version.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { getEmploymentPromises } from "../../../components/employmentPromises/employmentPromisesSlice";
import MobileEmploymentPromisesAddModal from "./MobileEmploymentPromisesAddModal";
import MobileEmploymentPromisesEditModal from "./MobileEmploymentPromisesEditModal";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

const PAGE_SIZE = 20;

// Progression color mapping
const getProgressionColor = (progression) => {
  switch (progression) {
    case "Attente d'envoi":
      return "warning";
    case "Envoyé":
    case "Envoye":
      return "info";
    case "Reçu":
    case "Recu":
      return "success";
    default:
      return "default";
  }
};

export default function MobileEmploymentPromisesList({ addVisible, setAddVisible }) {
  const dispatch = useDispatch();
  const mounted = useRef();

  const list = useSelector((state) => state.employmentPromises.list);

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [onViewPromise, setOnViewPromise] = useState(null);
  const [onViewVisible, setOnViewVisible] = useState(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      dispatch(getEmploymentPromises());
    }
  });

  const filteredData = useMemo(() => {
    const items = list || [];
    if (!searchText.trim()) return items;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return items.filter((p) => {
      const searchStr = [
        p.family_name,
        p.name,
        p.email,
        p.company_name,
        p.progression,
        p.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => searchStr.includes(t));
    });
  }, [list, searchText]);

  React.useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const handleCardClick = (promise) => {
    setOnViewVisible(true);
    setOnViewPromise(promise);
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        // Clears the home indicator so the pagination row stays tappable.
        pb: "calc(20px + env(safe-area-inset-bottom))",
        mt: "130px",
        // 100vh overshoots the visual viewport on iOS Safari (the browser toolbar is
        // not subtracted), which pushed the pagination row permanently off-screen in
        // this fixed-height, non-page-scrolling layout. dvh tracks the visual
        // viewport; the vh declaration stays as the fallback.
        height: "calc(100vh - 130px)",
        "@supports (height: 100dvh)": { height: "calc(100dvh - 130px)" },
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover fieldset": {
              borderColor: "rgba(0,0,0,0.15)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "warning.main",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
            },
          },
        }}
      />

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, px: 0.5, fontWeight: 500, letterSpacing: "0.02em", opacity: 0.7 }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 10,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.12)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedData.map((promise, index) => (
          <Fade in key={promise.id} timeout={200 + index * 40}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:active": {
                  transform: "scale(0.98)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                },
              }}
            >
              <CardActionArea
                onClick={() => handleCardClick(promise)}
                sx={{ p: 0 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Name + Status badge */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: "0.9rem", letterSpacing: "-0.01em" }}
                    >
                      {promise.name} {promise.family_name}
                    </Typography>
                    {promise.status && (
                      <Chip
                        label={promise.status}
                        size="small"
                        color={promise.status === "OK" ? "success" : "error"}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: 24,
                          minWidth: 36,
                          borderRadius: "8px",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                  </Box>

                  {/* Company + email */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, opacity: 0.75 }}
                  >
                    {promise.company_name}
                    {promise.email ? ` - ${promise.email}` : ""}
                  </Typography>

                  {/* Progression tag */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
                    {promise.progression && (
                      <Chip
                        label={promise.progression}
                        size="small"
                        color={getProgressionColor(promise.progression)}
                        sx={{ fontSize: "0.7rem", height: 24, fontWeight: 600, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                      />
                    )}
                  </Box>

                  {/* Dates row */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.5, borderTop: "1px solid rgba(0,0,0,0.04)", pt: 0.75 }}>
                    {promise.request_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                        <Langs str="REQUEST_DATE" />:{" "}
                        {moment(promise.request_date).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    {promise.start_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                        <Langs str="START_DATE" />:{" "}
                        {moment(promise.start_date).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    {promise.sent_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                        <Langs str="SENT_DATE" />:{" "}
                        {moment(promise.sent_date).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                minWidth: 40,
                height: 40,
                margin: "0 3px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}

      {/* Modals */}
      <MobileEmploymentPromisesAddModal
        visible={addVisible}
        setVisible={setAddVisible}
      />
      <MobileEmploymentPromisesEditModal
        visible={onViewVisible}
        setVisible={setOnViewVisible}
        onViewItem={onViewPromise}
        setOnViewItem={setOnViewPromise}
      />
    </Box>
  );
}
